import type { FC, ReactNode } from 'react'

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import dynamic from 'next/dynamic'
import { detectWebGL } from '@/hooks/use-webgl-support'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

export type StageStatus = 'idle' | 'loading' | 'ready' | 'unsupported'

interface StageContextValue {
    status: StageStatus
    reducedMotion: boolean
}

const StageContext = createContext<StageContextValue>({ status: 'idle', reducedMotion: false })

const StageCanvas = dynamic(() => import('@/components/three/stage-canvas'), { ssr: false })

type IdleWindow = Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
    cancelIdleCallback?: (handle: number) => void
}

/* Mounts the shared 3D canvas once the page has painted, or reports that
   this device cannot draw WebGL so every product falls back to 2D art. */
export const StageProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [status, setStatus] = useState<StageStatus>('idle')
    const [mountCanvas, setMountCanvas] = useState(false)
    const reducedMotion = useReducedMotion()

    useEffect(() => {
        if (detectWebGL() === 'unsupported') {
            setStatus('unsupported')

            return
        }

        setStatus('loading')

        const idleWindow = window as IdleWindow
        const start = () => setMountCanvas(true)

        if (idleWindow.requestIdleCallback) {
            const handle = idleWindow.requestIdleCallback(start, { timeout: 1200 })

            return () => idleWindow.cancelIdleCallback?.(handle)
        }

        const handle = window.setTimeout(start, 250)

        return () => window.clearTimeout(handle)
    }, [])

    const value = useMemo(() => ({ status, reducedMotion }), [status, reducedMotion])

    return (
        <StageContext.Provider value={value}>
            {children}

            {mountCanvas && status !== 'unsupported' && (
                <StageCanvas onReady={() => setStatus('ready')} onLost={() => setStatus('unsupported')} />
            )}
        </StageContext.Provider>
    )
}

export const useStage = (): StageContextValue => useContext(StageContext)
